import { css, cx } from "styled-system/css";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Check, X, Loader2 } from "lucide-react";
import { FadeIn } from "@/components/ui/animated";
import { InfoTooltip } from "@/components/ui/tooltip";
import { PasswordInput } from "@/components/ui/password-input";
import { cf } from "@/components/brand/sfturnos-styles";
import { useAuth } from "@/providers/AuthProvider";
import { displayApiError } from "@/lib/client-safe-error";
import { slugify } from "@/lib/slugify";
import { useToast } from "@/hooks/useToast";

type SlugStatus = "idle" | "checking" | "available" | "taken";

type PlanRow = {
  id: string;
  code: string;
  name: string;
};

type PlanPricingItem = {
  plan: "FREE" | "PRO" | "ENTERPRISE";
  monthlyPrice: number;
  annualPrice: number;
  extraAgendaMonthly: number;
  extraAgendaAnnual: number;
};

const ENTERPRISE_INCLUDED_AGENDAS = 3;
/** Con 0 agendas extra, Enterprise sería idéntico a Pro (misma capacidad, mismo precio base). */
const ENTERPRISE_MIN_EXTRA_AGENDAS = 1;

function formatPrice(n: number) {
  return new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(n);
}

export function RegisterPage() {
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const planFromUrl = searchParams.get("plan");
  const [plans, setPlans] = useState<PlanRow[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<string>("FREE");
  const [accountType, setAccountType] = useState<"PROFESSIONAL" | "FACILITY">("PROFESSIONAL");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slugInput, setSlugInput] = useState("");
  const [slugStatus, setSlugStatus] = useState<SlugStatus>("idle");
  const [referralCode, setReferralCode] = useState(() => searchParams.get("ref") ?? "");
  const [pricing, setPricing] = useState<PlanPricingItem[]>([]);
  const [extraAgendas, setExtraAgendas] = useState(ENTERPRISE_MIN_EXTRA_AGENDAS);

  useEffect(() => {
    const trimmed = slugInput.trim();
    if (!trimmed) {
      setSlugStatus("idle");
      return;
    }
    setSlugStatus("checking");
    const t = setTimeout(() => {
      void fetch(`/api/public/slug-available?slug=${encodeURIComponent(trimmed)}`)
        .then((r) => r.json())
        .then((d: { available: boolean }) => {
          setSlugStatus(d.available ? "available" : "taken");
        })
        .catch(() => setSlugStatus("idle"));
    }, 400);
    return () => clearTimeout(t);
  }, [slugInput]);

  useEffect(() => {
    void fetch("/api/public/landing", { cache: "no-store" })
      .then(async (r) => {
        if (!r.ok) throw new Error("landing");
        return r.json() as Promise<{ plans?: PlanRow[] }>;
      })
      .then((d) => {
        const list = Array.isArray(d.plans) ? d.plans : [];
        setPlans(list);
        const wanted = planFromUrl;
        if (wanted && list.some((p) => p.code === wanted)) {
          setSelectedPlan(wanted);
        } else if (list.some((p) => p.code === "FREE")) {
          setSelectedPlan("FREE");
        } else if (list.length > 0) {
          setSelectedPlan(list[0].code);
        }
      })
      .catch(() => {
        setPlans([]);
        if (planFromUrl) setSelectedPlan(planFromUrl);
      });
  }, [planFromUrl]);

  useEffect(() => {
    void fetch("/api/public/plan-pricing", { cache: "no-store" })
      .then((r) => r.json() as Promise<{ pricing?: PlanPricingItem[] }>)
      .then((d) => setPricing(Array.isArray(d.pricing) ? d.pricing : []))
      .catch(() => setPricing([]));
  }, []);

  useEffect(() => {
    if (selectedPlan !== "ENTERPRISE") setExtraAgendas(ENTERPRISE_MIN_EXTRA_AGENDAS);
  }, [selectedPlan]);

  useEffect(() => {
    if (!loading && user) {
      navigate(user.role === "SUPERADMIN" ? "/admin" : "/dashboard", { replace: true });
    }
  }, [loading, user, navigate]);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (slugStatus === "taken") {
      setError("Ese enlace público ya está en uso. Probá con otro.");
      return;
    }
    if (slugStatus === "checking") {
      setError("Esperá un momento, estamos verificando el enlace público.");
      return;
    }
    const fd = new FormData(e.currentTarget);
    const rawSlug = String(fd.get("slug") ?? "").trim();
    const trimmedReferralCode = referralCode.trim();
    const plan = String(fd.get("plan") ?? "FREE");
    const rawPhone = String(fd.get("phone") ?? "").trim();
    const body = {
      name: String(fd.get("name")).trim(),
      slug: rawSlug ? slugify(rawSlug) : "",
      email: String(fd.get("email")).trim().toLowerCase(),
      password: String(fd.get("password")),
      phone: rawPhone || undefined,
      plan,
      accountType,
      referralCode: trimmedReferralCode,
      ...(plan === "ENTERPRISE" ? { extraAgendas } : {}),
    };
    setBusy(true);
    const r = await fetch("/api/auth/register", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      credentials: "include",
    });
    const data = (await r.json()) as { error?: string; referralApplied?: boolean };
    setBusy(false);
    if (!r.ok) {
      setError(displayApiError(data.error, "No se pudo registrar. Intentá de nuevo."));
      return;
    }
    if (trimmedReferralCode && !data.referralApplied) {
      toast.info("Código de referido no válido, tu registro continúa igual.");
    }
    await refresh();
    navigate("/dashboard", { replace: true });
  }

  return (
    <main
      className={css({
        minH: "100vh",
        display: "grid",
        gridTemplateColumns: { base: "1fr", md: "1fr 1fr" },
        bg: "#f4f6f8",
      })}
    >
      <section
        className={cx(
          cf.darkPanel,
          css({
            display: { base: "none", md: "flex" },
            flexDir: "column",
            justifyContent: "space-between",
            p: "10",
          }),
        )}
      >
        <div className={cf.darkPanelInner}>
          <p className={css({ fontSize: "xs", letterSpacing: "widest", opacity: 0.65, mb: "3" })}>SFTURNOS</p>
          <h1 className={css({ fontSize: "5xl", fontWeight: "bold", letterSpacing: "tight", lineHeight: "1", mb: "4" })}>SFTURNOS</h1>
          <p className={cf.subtitleOnDark}>Creá tu página de reservas, cobrá señas y enviá recordatorios sin complicarte la vida.</p>
        </div>
        <div className={cx(cf.darkPanelInner, css({ display: "flex", gap: "2", flexWrap: "wrap" }))}>
          <span className={cf.tag}>Sin tarjeta para empezar</span>
          <span className={cf.tag}>Panel simple</span>
          <span className={cf.tag}>Soporte humano</span>
        </div>
      </section>

      <section
        className={css({ display: "flex", alignItems: "center", justifyContent: "center", p: { base: "6", md: "12" }, bg: "linear-gradient(180deg, #f8fafc, #eef2f7)" })}
      >
        <FadeIn>
          <div className={css({ width: "100%", maxW: "560px", p: { base: "2", md: "4" } })}>
            <div className={css({ mb: "8" })}>
              <p className={cf.labelCaps}>NUEVA CUENTA</p>
              <h2 className={css({ fontSize: { base: "3xl", md: "4xl" }, fontWeight: "bold", color: "#0f172a", lineHeight: "1.05" })}>Registrate</h2>
              <p className={css({ fontSize: "md", color: "#64748b", mt: "2" })}>Completá los datos y empezá a recibir turnos online.</p>
            </div>

            <div className={cx(cf.card, cf.cardPadding)}>
              {error ? <p className={css({ fontSize: "sm", color: "red.600", mb: "4" })}>{error}</p> : null}

              <div className={css({ mb: "6" })}>
                <p className={css({ fontSize: "sm", fontWeight: "medium", color: "#0f172a", mb: "3" })}>Tipo de cuenta</p>
                <div className={css({ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "3" })}>
                  {(["PROFESSIONAL", "FACILITY"] as const).map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setAccountType(type)}
                      className={css({
                        p: "3", rounded: "lg", borderWidth: "2px",
                        borderColor: accountType === type ? "#4f46e5" : "#e2e8f0",
                        bg: accountType === type ? "#eef2ff" : "white",
                        textAlign: "left", cursor: "pointer",
                      })}
                    >
                      <p className={css({ fontSize: "sm", fontWeight: "semibold", color: "#0f172a" })}>
                        {type === "PROFESSIONAL" ? "Soy profesional" : "Tengo una instalación"}
                      </p>
                      <p className={css({ fontSize: "xs", color: "#64748b", mt: "0.5" })}>
                        {type === "PROFESSIONAL" ? "Terapeuta, coach, instructor..." : "Canchas, salas, espacios..."}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              <form onSubmit={(e) => void onSubmit(e)} className={css({ display: "flex", flexDir: "column", gap: "4" })}>
                <label className={css({ display: "flex", flexDir: "column", gap: "1.5" })}>
                  <span className={css({ fontSize: "sm", fontWeight: "medium", color: "#0f172a" })}>
                    {accountType === "FACILITY" ? "Nombre de la instalación" : "Nombre público"}
                  </span>
                  <input name="name" required minLength={2} className={cf.input}
                    placeholder={accountType === "FACILITY" ? "Mi club de pádel" : "Tu estudio o nombre"} />
                </label>
                <label className={css({ display: "flex", flexDir: "column", gap: "1.5" })}>
                  <span
                    className={css({
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "1.5",
                      fontSize: "sm",
                      fontWeight: "medium",
                      color: "#0f172a",
                    })}
                  >
                    Enlace público
                    <InfoTooltip text="Es la URL que vas a compartir con tus clientes para que reserven turnos: sfturnos.com/tu-enlace. Si lo dejás vacío, lo generamos automáticamente a partir del nombre." />
                    <span className={css({ fontWeight: "normal", color: "#94a3b8", fontSize: "xs" })}>(opcional)</span>
                  </span>
                  <div className={css({ position: "relative" })}>
                    <input
                      name="slug"
                      value={slugInput}
                      onChange={(e) => setSlugInput(e.target.value)}
                      pattern="[a-zA-Z0-9À-ÿ\s-]*"
                      className={cx(cf.input, css({ width: "100%", pr: "10" }))}
                      placeholder="Se genera del nombre si lo dejás vacío"
                    />
                    <span
                      className={css({
                        position: "absolute",
                        right: "3",
                        top: "50%",
                        transform: "translateY(-50%)",
                        display: "flex",
                        alignItems: "center",
                      })}
                    >
                      {slugStatus === "checking" ? (
                        <Loader2 size={16} className={css({ color: "#94a3b8", animation: "spin 1s linear infinite" })} aria-hidden />
                      ) : slugStatus === "available" ? (
                        <Check size={16} className={css({ color: "#16a34a" })} aria-label="Disponible" />
                      ) : slugStatus === "taken" ? (
                        <X size={16} className={css({ color: "#dc2626" })} aria-label="Ocupado" />
                      ) : null}
                    </span>
                  </div>
                  {slugStatus === "taken" ? (
                    <p className={css({ fontSize: "xs", color: "#dc2626" })}>Este enlace ya está en uso, probá con otro.</p>
                  ) : slugStatus === "available" ? (
                    <p className={css({ fontSize: "xs", color: "#16a34a" })}>¡Disponible!</p>
                  ) : null}
                </label>
                <label className={css({ display: "flex", flexDir: "column", gap: "1.5" })}>
                  <span className={css({ fontSize: "sm", fontWeight: "medium", color: "#0f172a" })}>Email</span>
                  <input name="email" type="email" required autoComplete="email" className={cf.input} placeholder="tu@email.com" />
                </label>
                <label className={css({ display: "flex", flexDir: "column", gap: "1.5" })}>
                  <span className={css({ fontSize: "sm", fontWeight: "medium", color: "#0f172a" })}>
                    Teléfono{" "}
                    <span className={css({ fontWeight: "normal", color: "#94a3b8", fontSize: "xs" })}>(opcional)</span>
                  </span>
                  <input
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    className={cf.input}
                    placeholder="Ej: 3794556599"
                  />
                </label>
                <label className={css({ display: "flex", flexDir: "column", gap: "1.5" })}>
                  <span className={css({ fontSize: "sm", fontWeight: "medium", color: "#0f172a" })}>Contraseña</span>
                  <PasswordInput
                    name="password"
                    required
                    minLength={8}
                    autoComplete="new-password"
                    className={cf.input}
                    placeholder="Mínimo 8 caracteres"
                  />
                </label>
                <label className={css({ display: "flex", flexDir: "column", gap: "1.5" })}>
                  <span className={css({ fontSize: "sm", fontWeight: "medium", color: "#0f172a" })}>Plan inicial</span>
                  <select name="plan" className={cf.input} value={selectedPlan} onChange={(e) => setSelectedPlan(e.target.value)}>
                    {plans.length === 0 ? (
                      <>
                        <option value="FREE">Free — 1 agenda, email</option>
                        <option value="PRO">Pro — 3 agendas, WhatsApp + pagos</option>
                        <option value="ENTERPRISE">Enterprise — agendas y soporte a medida</option>
                      </>
                    ) : (
                      plans
                        .filter((p) => ["FREE", "PRO", "ENTERPRISE"].includes(p.code))
                        .map((p) => (
                          <option key={p.id} value={p.code}>
                            {p.name}
                          </option>
                        ))
                    )}
                  </select>
                  {selectedPlan === "ENTERPRISE" ? (
                    (() => {
                      const enterprisePricing = pricing.find((p) => p.plan === "ENTERPRISE");
                      const basePrice = enterprisePricing?.monthlyPrice ?? 25000;
                      const extraPrice = enterprisePricing?.extraAgendaMonthly ?? 30000;
                      const total = basePrice + extraAgendas * extraPrice;
                      return (
                        <div
                          className={css({
                            mt: "1",
                            p: "4",
                            rounded: "lg",
                            bg: "#faf5ff",
                            borderWidth: "1px",
                            borderColor: "#e9d5ff",
                          })}
                        >
                          <label className={css({ display: "flex", flexDir: "column", gap: "2" })}>
                            <span className={css({ fontSize: "sm", fontWeight: "semibold", color: "#334155" })}>
                              Agendas extra
                            </span>
                            <div className={css({ display: "flex", alignItems: "center", gap: "3" })}>
                              <button
                                type="button"
                                onClick={() => setExtraAgendas((v) => Math.max(ENTERPRISE_MIN_EXTRA_AGENDAS, v - 1))}
                                className={css({
                                  w: "9", h: "9", rounded: "full",
                                  borderWidth: "1px", borderColor: "#e2e8f0",
                                  bg: "white", fontWeight: "bold", fontSize: "lg",
                                  cursor: extraAgendas <= ENTERPRISE_MIN_EXTRA_AGENDAS ? "not-allowed" : "pointer",
                                  opacity: extraAgendas <= ENTERPRISE_MIN_EXTRA_AGENDAS ? 0.4 : 1,
                                  _hover: { bg: "#f8fafc" },
                                })}
                              >
                                −
                              </button>
                              <span className={css({ fontSize: "xl", fontWeight: "bold", color: "#0f172a", minW: "2.5rem", textAlign: "center" })}>
                                {extraAgendas}
                              </span>
                              <button
                                type="button"
                                onClick={() => setExtraAgendas((v) => v + 1)}
                                className={css({
                                  w: "9", h: "9", rounded: "full",
                                  borderWidth: "1px", borderColor: "#e2e8f0",
                                  bg: "white", fontWeight: "bold", fontSize: "lg",
                                  cursor: "pointer",
                                  _hover: { bg: "#f8fafc" },
                                })}
                              >
                                +
                              </button>
                            </div>
                          </label>
                          <div className={css({ mt: "3", pt: "3", borderTopWidth: "1px", borderColor: "#e9d5ff", fontSize: "xs", color: "#7c3aed", lineHeight: "1.6" })}>
                            <p>Plan Enterprise: {ENTERPRISE_INCLUDED_AGENDAS} agendas incluidas — {formatPrice(basePrice)}/mes</p>
                            <p>+ {extraAgendas} agenda{extraAgendas !== 1 ? "s" : ""} extra × {formatPrice(extraPrice)}/mes = {formatPrice(extraAgendas * extraPrice)}/mes</p>
                          </div>
                          <p className={css({ mt: "2", pt: "2", borderTopWidth: "1px", borderColor: "#e9d5ff", fontSize: "lg", fontWeight: "bold", color: "#6d28d9" })}>
                            Total: {formatPrice(total)}/mes
                          </p>
                        </div>
                      );
                    })()
                  ) : null}
                </label>
                <label className={css({ display: "flex", flexDir: "column", gap: "1.5" })}>
                  <span
                    className={css({ fontSize: "sm", fontWeight: "medium", color: "#0f172a" })}
                  >
                    ¿Tenés un código de referido?{" "}
                    <span className={css({ fontWeight: "normal", color: "#94a3b8", fontSize: "xs" })}>(opcional)</span>
                  </span>
                  <input
                    name="referralCode"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value)}
                    className={cx(cf.input, css({ textTransform: "uppercase" }))}
                    placeholder="JUAN10"
                  />
                </label>
                <button
                  type="submit"
                  disabled={busy || slugStatus === "taken" || slugStatus === "checking"}
                  className={css({
                    h: "12",
                    rounded: "lg",
                    fontWeight: "semibold",
                    bg: "#0f172a",
                    color: "white",
                    border: "none",
                    cursor: busy ? "wait" : "pointer",
                    _disabled: { opacity: 0.6, cursor: "not-allowed" },
                  })}
                >
                  {busy ? "Creando cuenta…" : "Crear cuenta"}
                </button>
              </form>
              <p className={css({ mt: "6", pt: "4", borderTopWidth: "1px", borderColor: "#e2e8f0", fontSize: "sm", color: "#64748b", textAlign: "center" })}>
                ¿Ya tenés cuenta?{" "}
                <Link to="/auth/login" className={css({ color: "#0f172a", fontWeight: "semibold", textDecoration: "underline" })}>
                  Ingresar
                </Link>
              </p>
            </div>
          </div>
        </FadeIn>
      </section>
    </main>
  );
}
